import { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { FormProvider, useForm } from 'react-hook-form';

import Button from '@/components/Button';
import FilterDrawer from '@/components/FilterDrawer';

import { useDashboard } from '@/hooks/useDashboard';
import { IsExportingProvider } from '@/hooks/useIsExporting';

const Drawer = dynamic( () => import( '@/components/Drawer' ), { ssr: false } );

type TagProps = {
	filterKey: string;
	filterValue: unknown;
	onRemove?: any;
};

const filterKeyMap: Record<string, string> = {
	'underwriter': 'Underwriter',
	'programCode': 'Program Code',
	'coverage': 'Coverage',
	'agent': 'Agent'
};

function Tag({
	filterKey,
	filterValue,
	onRemove = () => {}
}: TagProps ) {
	const labelString = useMemo( () => {
		let filterValues = '';

		if ( Array.isArray( filterValue ) ) {
			filterValues = filterValue.map( filter => {
				return filter.label || filter.value || filter;
			}).join( ', ' );
		}

		return filterValues;
	}, [ filterValue ] );

	if ( labelString === '' ) {
		return null;
	}

	return (
		<div className="inline-flex items-center py-1 pl-4 text-sm font-bold">
			<div className="rounded-l-lg bg-rural-gray-300 py-1 pl-4 pr-2">
				{ filterKeyMap[ filterKey ] }: { labelString }
			</div>

			<button className="rounded-r-lg bg-rural-gray-300 py-1 px-3 hover:bg-rural-gray-500" onClick={ () => onRemove( filterKey ) }>&times;</button>
		</div>
	);
}

export default function Filters() {
	const { filters, setFilters } = useDashboard();
	const [ isFilterOpen, setIsFilterOpen ] = useState( false );

	const toggleFilter = useCallback( () => {
		setIsFilterOpen( !isFilterOpen );
	}, [ isFilterOpen, setIsFilterOpen ] );

	const removeFilter = useCallback( ( filterKey: string ) => {
		const currentFilters: any = { ...filters };
		
		if ( currentFilters[ filterKey ] ) {
			delete currentFilters[ filterKey ];
		}

		setFilters( currentFilters );
	}, [ filters, setFilters ] );

	const activeFilters = useMemo( () => {
		return Object.entries( filters ).filter( ( [ , val ]: [ unknown, any ] ) => val !== undefined && val?.length > 0 );
	}, [ filters ] );

	const methods = useForm();

	return (
		<>
			<div className="flex gap-2 flex-wrap items-center">
				<Button type="button" variant="outline" size="medium" onClick={ () => { toggleFilter() } }>
					{ `${ activeFilters.length > 0 ? 'Update' : 'Add' } filters` }
				</Button>

				{ activeFilters.map( ( [ key, val ] ) => (
					<Tag
						key={ key }
						filterKey={ key }
						filterValue={ val }
						onRemove={ removeFilter }
					/>
				))}
			</div>

			<Drawer
				isOpen={ isFilterOpen }
				onClose={ () => setIsFilterOpen( false ) }
				title="Apply Filters"
			>
				<IsExportingProvider isPdfExport={ true }>
					<FormProvider { ...methods }>
						<FilterDrawer
							onAccept={ () => setIsFilterOpen( false ) }
						/>
					</FormProvider>
				</IsExportingProvider>
			</Drawer>
		</>
	);
}
